import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { Grid, Typography, Select } from "@mui/material";

import CachedIcon from "@mui/icons-material/Cached";

import { ReactComponent as GeneralIcon } from "../../../vendor/waf/general.svg";
import { ReactComponent as RuleIcon } from "../../../vendor/waf/rule.svg";
import { ReactComponent as ConfirmIcon } from "../../../vendor/button/confirm.svg";

import { ConfigAction, SslType, UserType } from "../../../utils/constants";
import useZone from "../../../hooks/user/useZone";
import useAuth from "../../../hooks/useAuth";
import useLLMSSLConfig from "../../../hooks/user/useLLMSSLConfig";
import { Button, MenuItem, SnackbarAlert } from "../../../components/pages/common/styled";

const tabInfo = [
  { url: "ssl", icon: <RuleIcon />, title: "Overview" },
  { url: "ssl/config", icon: <GeneralIcon />, title: "Configuration" },
];

function LLMSSLConfigHeader({ title, url, children }) {
  const navigate = useNavigate();

  const { configZone } = useParams();
  const zoneUid = configZone;
  const { zoneList, settingApply, getZonesForItems } = useZone();

  const { isAuthenticated, userType } = useAuth();
  const { sslConfig, getSSLConfig, setErr, errMsg } = useLLMSSLConfig();

  const [snackOpen, setSnackOpen] = React.useState(false);
  const handleSnackClose = () => {
    setSnackOpen(false);
    setErr(null);
  };
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getZonesForItems();
    }
    return () => setErr(null);
  }, [isAuthenticated, getZonesForItems, setErr]);

  React.useEffect(() => {
    if (errMsg) {
      setSnackOpen(true);
      setMessage(errMsg);
      setSuccess("error");
    } else {
      setSnackOpen(false);
      setMessage(null);
    }
  }, [errMsg]);

  const selectCurrentZone = (event) => {
    setErr(null);
    navigate(`/llm/${event.target.value}/${url}`);
  };
  const refresh = () => {
    if (zoneUid) {
      getSSLConfig(zoneUid, true);
    }
  };
  const apply = async () => {
    if (sslConfig.ssl_type !== SslType.OFF) {
      if (sslConfig?.certs === undefined) {
        setErr("You must upload or generate your own certificate");
        return;
      }
    }
    if (sslConfig.ssl_type === SslType.FULL_STRICT) {
      if (sslConfig.sg_certs === undefined) {
        setErr("You must generate your own original certificate to use Strict Full SSL");
        return;
      }
    }
    setLoading(true);
    const zone = zoneList.find((s) => s.id === zoneUid);
    if (zone) {
      const result = await settingApply(zone.zone_id, ConfigAction.SSL);
      setMessage(result.msg);
      setSuccess(result.status);
    }
    setLoading(false);
    setSnackOpen(true);
  };
  const gotoSSLPage = (linkURL) => {
    navigate(`/llm/${zoneUid}/${linkURL}`);
  };
  return (
    <React.Fragment>
      <Helmet title={url === "ssl/config" ? "SSL Configuration" : "SSL Overview"} />
      <Grid container sx={{ display: "flex", alignItems: "center" }} mt={9}>
        <Grid item>
          <Typography variant="h1" display="inline">
            {title}
          </Typography>
        </Grid>
        <Grid item xs></Grid>
        <Grid item display="flex" alignItems="center">
          <Select value={zoneUid} onChange={selectCurrentZone} sx={{ width: "320px", border: "none" }}>
            {zoneList?.map((zone, i) => {
              return (
                <MenuItem key={i} value={zone.id}>
                  {zone.zone_id}
                </MenuItem>
              );
            })}
          </Select>
        </Grid>
      </Grid>
      <Grid container mt={6}>
        {tabInfo.map((info, idx) => {
          return (
            <>
              {idx > 0 ? <Grid width={"8px"} /> : <></>}
              <Grid xs>
                <Button
                  variant={url === info.url ? "contained" : "outlined"}
                  color="tab"
                  onClick={() => gotoSSLPage(info.url)}
                  sx={{ width: "100%", height: "106px", borderWidth: "0px", borderRadius: "8px", padding: 1 }}
                >
                  <Grid container>
                    <Grid xs={12}>{info.icon}</Grid>
                    <Grid xs={12}>
                      <Typography variant="h2" pl="8px" sx={{ fontSize: "15px" }}>
                        {info.title}
                      </Typography>
                    </Grid>
                  </Grid>
                </Button>
              </Grid>
            </>
          );
        })}
      </Grid>
      {children}
      <Grid container mt={15}>
        <Grid item xs />
        <Grid item>
          <Button variant="contained" color="warning" size="ui" startIcon={<CachedIcon />} ml={4} onClick={refresh}>
            Refresh
          </Button>
          {UserType.ORG_USER === userType ? (
            <></>
          ) : (
            <Button
              variant="contained"
              color="success"
              size="ui"
              startIcon={<ConfirmIcon />}
              loadingPosition="start"
              loading={loading}
              onClick={apply}
              sx={{ marginLeft: "16px" }}
            >
              Apply
            </Button>
          )}
        </Grid>
      </Grid>
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default LLMSSLConfigHeader;
